import { useEffect, useState } from 'react';
import { logger } from '@/lib/logger';
import { useAppStore } from '@/stores/appStore';
import { projectSettingsService } from '@/services/projectSettingsService';
import { toast } from 'sonner';

export function useProjectIconStyle(floorPlanId: string | null) {
  const [projectId, setProjectId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  const iconStyle = useAppStore((state) => state.products.iconStyle);
  const setProductIconStyle = useAppStore((state) => state.setProductIconStyle);

  // Get project ID from floor plan ID
  useEffect(() => {
    async function fetchProjectId() {
      if (!floorPlanId) {
        setIsLoading(false);
        return;
      }

      const id = await projectSettingsService.getProjectIdFromFloorPlan(floorPlanId);
      setProjectId(id);
      setIsLoading(false);
    }

    fetchProjectId();
  }, [floorPlanId]);

  // Load icon style from project settings
  useEffect(() => {
    async function loadIconStyle() {
      if (!projectId) return;

      logger.log('[IconStyle] Loading icon style for project:', projectId);
      const settings = await projectSettingsService.getProjectSettings(projectId);
      logger.log('[IconStyle] Retrieved settings:', settings);
      
      if (settings?.icon_style) {
        logger.log('[IconStyle] Setting icon style to:', settings.icon_style);
        setProductIconStyle(settings.icon_style);
      } else {
        logger.log('[IconStyle] No icon style found in settings, using default');
      }
    }

    loadIconStyle();
  }, [projectId, setProductIconStyle]);

  // Save icon style when it changes
  useEffect(() => {
    async function saveIconStyle() {
      if (!projectId || isLoading) return;

      logger.log('[IconStyle] Saving icon style:', iconStyle, 'for project:', projectId);
      const success = await projectSettingsService.updateIconStyle(projectId, iconStyle);
      if (success) {
        logger.log('[IconStyle] Successfully saved icon style');
      } else {
        logger.error('[IconStyle] Failed to save icon style');
        toast.error('Failed to save icon style preference');
      }
    }

    // Debounce the save to avoid too many requests
    const timeoutId = setTimeout(saveIconStyle, 500);
    return () => clearTimeout(timeoutId);
  }, [iconStyle, projectId, isLoading]);

  return {
    iconStyle,
    setIconStyle: setProductIconStyle,
    isLoading,
    projectId
  };
}
